import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, FormControl, FormLabel, HStack, Input, InputGroup, InputLeftElement,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  NumberInput, NumberInputField, Select, Text, Textarea, VStack, useToast,
} from '@chakra-ui/react';
import { Gift, Search, Send } from 'lucide-react';
import gameApiService from '../../services/gameApiService';
import { POKER_COLORS } from '../../theme/designSystem';
import PokerButton from '../ui/PokerButton';

// ============================================================================
// ADMIN SEND COINS MODAL
// ----------------------------------------------------------------------------
// Owns its own form state. It used to live inside AdminDashboard, which meant
// every keystroke re-rendered the entire 8k-line dashboard (users table, stats,
// every tab) and typing lagged badly. Now a keystroke re-renders this modal
// and nothing else.
//
// Modes:
//   credit  - add coins, player gets a claim card (the original behavior)
//   bundle  - replace a failed purchase: $X PSS Coins + X*1000 PlayCoins at
//             once, same formula the store uses
//   delta   - silent +/- correction
//   set     - silent overwrite to an exact balance
// ============================================================================

export interface SendCoinsUser {
  id: string;
  username: string;
  email: string;
  playCoins: number;
  pkCash: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  users: SendCoinsUser[];
  /** Pre-selected player (from a row action or the audit drawer). */
  preset?: { id: string; username: string } | null;
  /** Called after a successful send so the dashboard can refresh. */
  onDone?: () => void | Promise<void>;
}

type Mode = 'credit' | 'bundle' | 'delta' | 'set' | 'voiceMinutes';
type Currency = 'playcoins' | 'pkcash';

const BUNDLE_PRESETS = [5, 10, 25, 50, 100];
const PKCASH_PRESETS = [5, 10, 15, 20, 25, 50];
const PLAYCOIN_PRESETS = [100, 500, 1000, 5000];
const VOICE_MINUTE_PRESETS = [15, 45, 90, 180];

const MODE_HINT: Record<Mode, string> = {
  credit: 'Credits the player and shows them a claim card.',
  bundle: 'Replaces a failed purchase: $X PSS Coins plus X,000 PlayCoins, exactly what the store would have delivered. Player gets a claim card.',
  delta: 'Silent correction. Use a NEGATIVE amount to take coins back.',
  set: 'Silent correction. Overwrites the balance with the exact amount below.',
  voiceMinutes: 'Adds free voice and video minutes to this week for the player (support tool). Player gets a push.',
};

const AdminSendCoinsModal: React.FC<Props> = ({ isOpen, onClose, users, preset, onDone }) => {
  const toast = useToast();
  const [selected, setSelected] = useState<{ id: string; username: string } | null>(null);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<Mode>('credit');
  const [currency, setCurrency] = useState<Currency>('playcoins');
  const [amount, setAmount] = useState(0);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Fresh form every time the modal opens; honor a preset player if given.
  useEffect(() => {
    if (!isOpen) return;
    setSelected(preset ? { id: preset.id, username: preset.username } : null);
    setSearch('');
    setMode('credit');
    setCurrency('playcoins');
    setAmount(0);
    setMessage('');
    setSending(false);
  }, [isOpen, preset]);

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter(u =>
        u.username.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.id.toLowerCase().includes(q))
      .slice(0, 10);
  }, [search, users]);

  const isCorrection = mode === 'delta' || mode === 'set';
  const presets = mode === 'voiceMinutes' ? VOICE_MINUTE_PRESETS : mode === 'bundle' ? BUNDLE_PRESETS : currency === 'pkcash' ? PKCASH_PRESETS : PLAYCOIN_PRESETS;
  const showDollar = mode === 'bundle' || (mode !== 'voiceMinutes' && currency === 'pkcash');

  const amountValid =
    mode === 'credit' || mode === 'bundle' || mode === 'voiceMinutes' ? amount > 0
      : mode === 'delta' ? amount !== 0
        : amount >= 0;

  const submitLabel = (() => {
    if (!amountValid) return mode === 'set' ? 'Set balance' : mode === 'delta' ? 'Adjust' : 'Send';
    if (mode === 'bundle') return `Send $${amount} PSS + ${(amount * 1000).toLocaleString()} PC`;
    if (mode === 'voiceMinutes') return `Grant ${amount} voice minutes`;
    const unit = currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins';
    const shown = currency === 'pkcash' ? `$${amount.toLocaleString()}` : amount.toLocaleString();
    if (mode === 'set') return `Set to ${shown} ${unit}`;
    if (mode === 'delta') return `${amount > 0 ? '+' : ''}${shown} ${unit}`;
    return `Send ${shown} ${unit}`;
  })();

  const handleSubmit = async () => {
    if (!selected) {
      toast({ title: 'Please select a user', status: 'error', duration: 3000 });
      return;
    }
    if (!amountValid) {
      toast({
        title: mode === 'delta' ? 'Enter a non-zero amount (negative to take away)'
          : mode === 'set' ? 'A set balance cannot be negative'
            : 'Amount must be greater than 0',
        status: 'error', duration: 3000,
      });
      return;
    }

    setSending(true);
    try {
      if (mode === 'voiceMinutes') {
        const r = await gameApiService.adminGrantVoiceMinutes(selected.id, amount);
        if (!r.success) {
          toast({ title: 'Could not grant minutes', description: r.error, status: 'error', duration: 5000 });
          return;
        }
        toast({ title: 'Voice minutes granted', description: `${amount} free minutes added for ${selected.username} this week`, status: 'success', duration: 5000 });
        onClose();
        await onDone?.();
        return;
      }
      if (isCorrection) {
        const r = await gameApiService.adminAdjustBalance(selected.id, currency, mode as 'set' | 'delta', amount, message || undefined);
        if (!r.success) {
          toast({ title: 'Adjustment failed', description: r.error, status: 'error', duration: 5000 });
          return;
        }
        const label = currency === 'pkcash' ? 'PSS Coins' : 'PlayCoins';
        toast({
          title: 'Balance adjusted',
          description: `${selected.username}: ${(r.previousBalance ?? 0).toLocaleString()} → ${(r.newBalance ?? 0).toLocaleString()} ${label}`,
          status: 'success', duration: 6000,
        });
      } else {
        const r = await gameApiService.adminSendCoins(
          selected.id,
          mode === 'bundle' ? 'bundle' : currency,
          amount,
          message || undefined,
        );
        if (!r.success) {
          toast({ title: 'Error sending coins', description: r.error, status: 'error', duration: 5000 });
          return;
        }
        toast({
          title: 'Coins Sent!',
          description: mode === 'bundle'
            ? `$${amount} PSS Coins + ${(amount * 1000).toLocaleString()} PlayCoins sent to ${selected.username}`
            : `${currency === 'pkcash' ? `$${amount.toLocaleString()} PSS Coins` : `${amount.toLocaleString()} PlayCoins`} sent to ${selected.username}`,
          status: 'success', duration: 5000,
        });
      }
      onClose();
      await onDone?.();
    } catch (error) {
      console.error('Send coins error:', error);
      toast({ title: 'Error sending coins', status: 'error', duration: 3000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg={POKER_COLORS.darkTeal} borderColor={POKER_COLORS.gold} border="2px">
        <ModalHeader color={POKER_COLORS.gold}><HStack><Gift size={24} /><Text>Send Coins</Text></HStack></ModalHeader>
        <ModalCloseButton color={POKER_COLORS.gold} />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel color="gray.300">Search User</FormLabel>
              <InputGroup>
                <InputLeftElement><Search size={16} color="#718096" /></InputLeftElement>
                <Input
                  placeholder="Search by username or email..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  bg={POKER_COLORS.darkBg}
                  borderColor={POKER_COLORS.gold}
                  color="white"
                />
              </InputGroup>
              {matches.length > 0 && (
                <Box mt={2} bg={POKER_COLORS.darkBg} borderRadius="md" maxH="200px" overflow="auto">
                  {matches.map(user => (
                    <Box
                      key={user.id}
                      p={3}
                      cursor="pointer"
                      _hover={{ bg: `${POKER_COLORS.gold}22` }}
                      onClick={() => { setSelected({ id: user.id, username: user.username }); setSearch(''); }}
                    >
                      <HStack justify="space-between">
                        <VStack align="start" spacing={0}>
                          <Text color="white" fontWeight="bold">{user.username}</Text>
                          <Text color="gray.500" fontSize="xs">{user.email}</Text>
                        </VStack>
                        <VStack align="end" spacing={0}>
                          <Text color={POKER_COLORS.cyan} fontSize="sm">{(user.playCoins || 0).toLocaleString()} PC</Text>
                          <Text color={POKER_COLORS.gold} fontSize="sm">${(user.pkCash || 0).toFixed(2)}</Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </Box>
              )}
              {selected && (
                <Box mt={2} p={3} bg={`${POKER_COLORS.gold}22`} borderRadius="md" border="1px solid" borderColor={POKER_COLORS.gold}>
                  <HStack justify="space-between">
                    <Text color={POKER_COLORS.gold} fontWeight="bold">Selected: {selected.username}</Text>
                    <Button size="xs" onClick={() => setSelected(null)}>Clear</Button>
                  </HStack>
                </Box>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="gray.300">Action</FormLabel>
              <Select
                value={mode}
                onChange={e => { setMode(e.target.value as Mode); setAmount(0); }}
                bg={POKER_COLORS.darkBg}
                borderColor={isCorrection ? '#f56565' : POKER_COLORS.gold}
                color="white"
              >
                <option value="credit" style={{ background: '#0D1B2A' }}>Send coins (adds, notifies player)</option>
                <option value="bundle" style={{ background: '#0D1B2A' }}>Replace failed purchase (PSS Coins + PlayCoins)</option>
                <option value="delta" style={{ background: '#0D1B2A' }}>Adjust by amount (+/- correction)</option>
                <option value="set" style={{ background: '#0D1B2A' }}>Set exact balance</option>
                <option value="voiceMinutes" style={{ background: '#0D1B2A' }}>Grant free voice minutes</option>
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>{MODE_HINT[mode]}</Text>
            </FormControl>

            {mode !== 'bundle' && mode !== 'voiceMinutes' && (
              <FormControl isRequired>
                <FormLabel color="gray.300">Currency</FormLabel>
                <Select
                  value={currency}
                  onChange={e => { setCurrency(e.target.value as Currency); setAmount(0); }}
                  bg={POKER_COLORS.darkBg}
                  borderColor={POKER_COLORS.gold}
                  color="white"
                >
                  <option value="playcoins" style={{ background: '#0D1B2A' }}>PlayCoins (Free-to-play)</option>
                  <option value="pkcash" style={{ background: '#0D1B2A' }}>PSS Coins (sweepstakes)</option>
                </Select>
              </FormControl>
            )}

            <FormControl isRequired>
              <FormLabel color="gray.300">{mode === 'bundle' ? 'Purchase amount (USD)' : mode === 'voiceMinutes' ? 'Minutes' : 'Amount'}</FormLabel>
              <NumberInput
                value={amount}
                onChange={(_, v) => setAmount(Number.isFinite(v) ? v : 0)}
                min={mode === 'delta' ? -10000000 : 0}
              >
                <NumberInputField
                  bg={POKER_COLORS.darkBg}
                  borderColor={POKER_COLORS.gold}
                  color="white"
                  placeholder={mode === 'bundle' ? 'e.g. 10 for the $10 pack' : 'Enter amount...'}
                />
              </NumberInput>
              <HStack mt={2} spacing={2} flexWrap="wrap">
                {presets.map(v => (
                  <Button
                    key={v}
                    size="xs"
                    variant={amount === v ? 'solid' : 'outline'}
                    colorScheme={showDollar ? 'yellow' : 'cyan'}
                    onClick={() => setAmount(v)}
                  >
                    {showDollar ? `$${v}` : v.toLocaleString()}
                  </Button>
                ))}
              </HStack>
              {mode === 'bundle' && amount > 0 && (
                <Text fontSize="sm" color={POKER_COLORS.cyan} mt={2}>
                  Delivers ${amount} PSS Coins + {(amount * 1000).toLocaleString()} PlayCoins
                </Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel color="gray.300">{isCorrection ? 'Reason (internal)' : 'Message to User'}</FormLabel>
              <Textarea
                placeholder={mode === 'bundle' ? 'e.g. Sorry about the failed purchase, here is your pack!' : isCorrection ? 'e.g. Accidental double credit' : 'e.g. Bonus for reporting a bug!'}
                value={message}
                onChange={e => setMessage(e.target.value)}
                bg={POKER_COLORS.darkBg}
                borderColor={POKER_COLORS.gold}
                color="white"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <PokerButton variant="secondary" onClick={onClose}>Cancel</PokerButton>
          <PokerButton
            variant="primary"
            onClick={handleSubmit}
            isLoading={sending}
            isDisabled={!selected || !amountValid}
            bg={isCorrection ? 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)' : 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)'}
            leftIcon={<Send size={18} />}
          >
            {submitLabel}
          </PokerButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminSendCoinsModal;
